import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, MailQuestion, Sparkles, Hash, BookText, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import { BUSINESS_TYPES as TYPES } from "@/lib/businessTypes";
import { MyPromptsDialog } from "@/components/inquiry/MyPromptsDialog";
import { ModerationBlockedDialog } from "@/components/inquiry/ModerationBlockedDialog";
import { BusinessLookup } from "@/components/inquiry/BusinessLookup";
import { InquiryCard } from "@/components/inquiry/InquiryCard";
import { ContactMethodSlider } from "@/components/inquiry/ContactMethodSlider";
import { InquiryFormActions } from "@/components/inquiry/InquiryFormActions";
import { OutboundCallPanel } from "@/components/inquiry/OutboundCallPanel";

const EMPTY_FORM = { business_name: "", business_email: "", business_phone: "", business_type: "Foodservice", incident_id: "none", notes: "", prompt_id: "", contact_method: "email" };
const labelCls = "text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground";
const inputCls = "mt-2 bg-background border-border rounded-sm focus-visible:ring-[#007AFF]";

export default function Inquiries() {
  const { refresh: refreshUser } = useAuth();
  const [inquiries, setInquiries] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [showMine, setShowMine] = useState(false);
  const [draft, setDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calling, setCalling] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [callCost, setCallCost] = useState(null);

  const loadCallCost = useCallback(() => api.get("/inquiries/call-cost").then(({ data }) => setCallCost(data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/inquiries").then(({ data }) => setInquiries(data)).catch(() => setInquiries([]));
    api.get("/incidents").then(({ data }) => setIncidents(data)).catch(() => {});
    api.get("/inquiry-prompts").then(({ data }) => setPrompts(data)).catch(() => {});
    loadCallCost();
  }, [loadCallCost]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedIncident = incidents.find((i) => i.id === form.incident_id);
  const shared = prompts.filter((p) => !p.is_private);
  const mine = prompts.filter((p) => p.is_private);
  const autoPrompt = shared.find((p) => p.business_types.includes(form.business_type)) || shared.find((p) => p.is_default) || shared[0];
  const activePrompt = prompts.find((p) => p.id === form.prompt_id) || autoPrompt;
  const activeCall = useMemo(() => (inquiries || []).find((q) => q.status === "calling"), [inquiries]);

  const payload = () => ({
    business_name: form.business_name.trim(),
    business_email: form.business_email.trim(),
    business_phone: form.business_phone.trim(),
    business_type: form.business_type,
    incident_id: form.incident_id === "none" ? null : form.incident_id,
    notes: form.notes,
    prompt_id: activePrompt?.id || null,
    message: draft.trim(),
    contact_method: form.contact_method,
  });

  const upsertList = useCallback((item) => setInquiries((list) => {
    const rest = (list || []).filter((q) => q.id !== item.id);
    return [item, ...rest];
  }), []);

  const resetForm = () => { setForm(EMPTY_FORM); setDraft(""); setEditingId(null); };

  const draftEmail = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/inquiries/draft", {
        business_name: form.business_name || "the business", business_type: form.business_type,
        incident_id: form.incident_id === "none" ? null : form.incident_id, notes: form.notes, prompt_id: activePrompt?.id || null,
      });
      setDraft(data.draft);
      toast.success("Draft ready — edit it freely, then send");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not draft the email");
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = editingId
        ? await api.put(`/inquiries/drafts/${editingId}`, payload())
        : await api.post("/inquiries/drafts", payload());
      upsertList(data);
      setEditingId(data.id);
      toast.success(data.incident_number ? `Saved with incident #${data.incident_number}` : "Inquiry saved as a draft");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (form.contact_method !== "email") return;
    if (draft.trim().length < 10) {
      toast.error("Draft the email first (or write at least a sentence)");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/inquiries", {
        business_name: form.business_name, business_email: form.business_email, business_phone: form.business_phone,
        business_type: form.business_type, message: draft.trim(),
        incident_id: form.incident_id === "none" ? null : form.incident_id,
        origin_url: window.location.origin, draft_id: editingId,
      });
      upsertList(data);
      resetForm();
      toast.success(`Inquiry sent to ${data.business_name} — you'll be emailed when they respond`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "moderation_blocked") { setBlocked(detail); return; }
      toast.error(formatApiErrorDetail(detail) || "Could not send the inquiry");
    } finally {
      setSending(false);
    }
  };

  const placeCall = async () => {
    setCalling(true);
    try {
      const { data } = await api.post("/inquiries/call", { ...payload(), draft_id: editingId });
      upsertList(data);
      resetForm();
      refreshUser();
      loadCallCost();
      toast.success(`Calling ${data.business_name} — the representative is dialing now`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not place the call");
    } finally {
      setCalling(false);
    }
  };

  const onCallFinished = useCallback((data) => {
    upsertList(data);
    refreshUser();
    loadCallCost();
    toast[data.status === "called" ? "success" : "info"](
      data.status === "called" ? `Call with ${data.business_name} ended — transcript saved` : `Call to ${data.business_name} didn't connect — credits refunded`);
  }, [upsertList, refreshUser, loadCallCost]);

  const edit = (q) => {
    setForm({
      business_name: q.business_name, business_email: q.business_email || "", business_phone: q.business_phone || "",
      business_type: TYPES.includes(q.business_type) ? q.business_type : "Other", incident_id: q.incident_id || "none",
      notes: q.notes || "", prompt_id: q.prompt_id || "", contact_method: q.contact_method || "email",
    });
    setDraft(q.message || "");
    setEditingId(q.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scheduleCallback = async (id, atIso) => {
    try {
      const { data } = await api.post(`/inquiries/${id}/callback`, { at: atIso });
      upsertList(data);
      toast.success(`Callback scheduled — the manager will hear ${data.callback.local_label}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not schedule the callback");
      throw err;
    }
  };

  const cancelCallback = async (id) => {
    try {
      const { data } = await api.delete(`/inquiries/${id}/callback`);
      upsertList(data);
      toast.success("Callback cancelled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel the callback");
    }
  };

  // Background refresh while something is pending server-side (scheduled callbacks, post-call AI review).
  const pending = useMemo(() => (inquiries || []).some((q) =>
    q.status === "callback_scheduled" || (q.call?.transcript && !q.call?.analyzed_at)), [inquiries]);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => api.get("/inquiries").then(({ data }) => setInquiries(data)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [pending]);

  const remove = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      setInquiries((list) => list.filter((q) => q.id !== id));
      if (editingId === id) resetForm();
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const isPhone = form.contact_method === "phone";
  const callFieldsReady = form.business_name.trim() && form.business_phone.trim() && form.incident_id !== "none";
  const hasFunds = !callCost || callCost.credits >= callCost.hold_amount;
  const canCall = isPhone && !!callFieldsReady && hasFunds && !activeCall && (callCost?.configured ?? true);
  const callHint = !isPhone ? null
    : activeCall ? "A call is already in progress."
    : callCost && !callCost.configured ? "Outbound calling isn't set up yet — ask an admin to pick the calling number."
    : !callFieldsReady ? "Phone mode: business name, business phone and a linked incident are required."
    : !hasFunds ? `Needs a $${callCost.hold_amount.toFixed(2)} credit block (you have $${callCost.credits.toFixed(2)}).`
    : `A $${callCost?.hold_amount?.toFixed(2)} block (${callCost?.block_minutes} min) is reserved; unused minutes are refunded.`;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="inquiries-page">
        <ModerationBlockedDialog block={blocked} onClose={() => setBlocked(null)} />
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] mb-2">Outbound Inquiries</p>
        <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">We reach out. They respond. You have a record.</h1>
        <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
          Send a calm, neutral "what happened?" to a business by email — or have an inquiry representative call
          them and walk through five questions. Their answers land here: verbatim, timestamped, on the record.
        </p>

        {activeCall && <OutboundCallPanel key={activeCall.id} inquiry={activeCall} onFinished={onCallFinished} />}

        <BusinessLookup onUse={(r) => setForm((f) => ({ ...f, business_name: r.name, business_email: r.emails[0] || f.business_email, business_phone: r.phones?.[0] || f.business_phone }))} />

        <form onSubmit={send} className="border border-border bg-[#0A0A0A] p-6 mb-12" data-testid="inquiry-form">
          {editingId && (
            <div className="flex items-center justify-between border border-[#007AFF]/30 bg-[#007AFF]/5 px-3 py-2 mb-4" data-testid="editing-banner">
              <p className="text-xs text-[#007AFF] font-semibold">Editing a saved draft — Save, Send, or Call will update this record.</p>
              <button type="button" onClick={resetForm} data-testid="editing-cancel-btn" className="text-muted-foreground hover:text-white" title="Start a new inquiry">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className={labelCls}>Business name</Label>
              <Input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                data-testid="inquiry-business-name" placeholder="Acme Foods Inc." className={inputCls} />
            </div>
            <div>
              <Label className={labelCls}>Business email {isPhone && <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>}</Label>
              <Input required={!isPhone} type="email" value={form.business_email} onChange={(e) => set("business_email", e.target.value)}
                data-testid="inquiry-business-email" placeholder="ops@acmefoods.com" className={inputCls} />
            </div>
          </div>

          <div className="mb-4">
            <Label className={labelCls}>Business phone {!isPhone && <span className="text-muted-foreground/60 normal-case tracking-normal">(optional)</span>}</Label>
            <Input type="tel" required={isPhone} value={form.business_phone} onChange={(e) => set("business_phone", e.target.value)}
              data-testid="inquiry-business-phone" placeholder="(505) 555-0142" className={inputCls} />
            <p className="text-[10px] text-muted-foreground mt-1.5">
              {isPhone ? "The inquiry representative dials this number." : "Kept on the record with this inquiry. Email is still how the request is sent."}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className={labelCls}>Call type</Label>
              <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v, prompt_id: "" }))}>
                <SelectTrigger data-testid="inquiry-type-select" className="mt-2 rounded-sm bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
                  {TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`inquiry-type-option-${t.toLowerCase()}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Linked incident {isPhone && <span className="text-[#34C759] normal-case tracking-normal">(required for calls)</span>}</Label>
              <Select value={form.incident_id} onValueChange={(v) => set("incident_id", v)}>
                <SelectTrigger data-testid="inquiry-incident-select" className="mt-2 rounded-sm bg-background border-border [&>span]:truncate [&>span]:text-left">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-border rounded-sm max-w-[calc(100vw-3rem)] sm:max-w-md">
                  <SelectItem value="none">— General (no incident) —</SelectItem>
                  {incidents.map((i) => (
                    <SelectItem key={i.id} value={i.id} data-testid={`inquiry-incident-option-${i.incident_number}`}>
                      <span className="font-mono">#{i.incident_number}</span>
                      {i.title && <span className="text-muted-foreground"> — {i.title}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedIncident && (
            <div className="flex items-center gap-2 mb-4 border border-[#007AFF]/30 bg-[#007AFF]/5 px-3 py-2" data-testid="inquiry-incident-name">
              <Hash className="w-3.5 h-3.5 text-[#007AFF] shrink-0" />
              <span className="text-sm font-mono text-[#007AFF]">{selectedIncident.incident_number}</span>
              <span className="text-sm truncate">{selectedIncident.title}</span>
            </div>
          )}

          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <Label className={labelCls}>Contact method</Label>
              <p className="text-[10px] text-muted-foreground mt-1" data-testid="contact-method-hint">
                {isPhone ? "An inquiry representative calls and asks five spoken questions." : "The request is emailed; they answer through a private link."}
              </p>
            </div>
            <ContactMethodSlider value={form.contact_method} onChange={(v) => set("contact_method", v)} />
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between">
              <Label className={`${labelCls} flex items-center gap-2`}>
                <BookText className="w-3.5 h-3.5 text-[#007AFF]" /> Drafting method
              </Label>
              <button type="button" onClick={() => setShowMine(true)} data-testid="inquiry-my-methods-btn"
                className="text-[11px] font-bold uppercase tracking-widest text-[#007AFF] hover:underline flex items-center gap-1">
                <Lock className="w-3 h-3" /> My methods{mine.length > 0 ? ` (${mine.length})` : ""}
              </button>
            </div>
            <Select value={activePrompt?.id || ""} onValueChange={(v) => set("prompt_id", v)} disabled={prompts.length === 0}>
              <SelectTrigger data-testid="inquiry-prompt-select" className="mt-2 rounded-sm bg-background border-border">
                <SelectValue placeholder="Loading methods…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">Shared library</SelectLabel>
                  {shared.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`inquiry-prompt-option-${p.id}`}>
                      {p.name}{p.is_default ? " · default" : ""}
                    </SelectItem>
                  ))}
                </SelectGroup>
                {mine.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">My methods</SelectLabel>
                    {mine.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`inquiry-prompt-option-${p.id}`}>{p.name}</SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            {activePrompt?.description && (
              <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="inquiry-prompt-description">{activePrompt.description}</p>
            )}
            {isPhone && (
              <p className="text-[11px] text-[#34C759] mt-1.5" data-testid="spoken-method-note">
                For calls, this method is simplified for spoken delivery — five short questions, one idea each.
              </p>
            )}
          </div>

          <div className="mb-4">
            <Label className={labelCls}>Notes for the draft (optional if an incident is linked)</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="inquiry-notes" rows={2}
              placeholder='e.g. "Order #4187 arrived Tuesday missing 12 cases — ask for their account."'
              className="mt-2 bg-background border-border rounded-sm resize-y focus-visible:ring-[#007AFF]" />
          </div>

          <div className={`mb-5 transition-opacity duration-200 ${isPhone ? "opacity-60" : ""}`}>
            <div className="flex items-center justify-between mb-2">
              <Label className={labelCls}>The email {isPhone && <span className="normal-case tracking-normal text-muted-foreground/60">(not used for calls)</span>}</Label>
              <Button type="button" size="sm" variant="outline" onClick={draftEmail} disabled={drafting} data-testid="inquiry-draft-btn"
                className="rounded-sm gap-1.5 h-7 text-xs border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 hover:text-[#007AFF]">
                {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {drafting ? "Drafting…" : "Draft email with AI"}
              </Button>
            </div>
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="inquiry-draft-textarea" rows={7}
              placeholder='Click "Draft email with AI" — or write your own. Fully editable; they receive exactly this text.'
              className="bg-background border-border rounded-sm resize-y leading-relaxed focus-visible:ring-[#007AFF]" />
            <p className="text-[11px] text-muted-foreground mt-2">
              Keep it factual and neutral — this exact text is what the business sees, framed as a no-drama request for their side of the story.
            </p>
          </div>

          <InquiryFormActions
            canSave={!!form.business_name.trim()} saving={saving} onSave={save} editingId={editingId}
            canCall={canCall} calling={calling} callHint={callHint} onCall={placeCall}
            canSend={!isPhone && draft.trim().length >= 10} sending={sending} />
        </form>

        {showMine && (
          <MyPromptsDialog prompts={prompts} onClose={() => setShowMine(false)}
            onChange={(next) => { setPrompts(next); if (!next.some((p) => p.id === form.prompt_id)) set("prompt_id", ""); }} />
        )}

        <h2 className="font-heading text-lg font-black tracking-tighter mb-4">Inquiries</h2>
        {inquiries === null ? (
          <p className="text-sm text-muted-foreground font-mono">Loading…</p>
        ) : inquiries.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center" data-testid="inquiries-empty">
            <MailQuestion className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No inquiries yet. Draft your first "what happened?" above.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="inquiries-list">
            {inquiries.map((q) => (
              <InquiryCard key={q.id} q={q} onDelete={remove} onEdit={edit}
                onScheduleCallback={scheduleCallback} onCancelCallback={cancelCallback} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
